import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type {
  ImportResource,
  Job,
  WorkspaceExport,
  WorkspaceExportRequest,
} from '@/api/types'
import { pushJob } from '@/features/job/queries'

/**
 * The archives a workspace has been exported into.
 *
 * Polled while any of them is still being made, for the same reason the job
 * list is: these carry their own progress and their own state, and the socket
 * says nothing about either. The interval is flat rather than derived -- an
 * export of a whole workspace takes minutes, so the first-second precision the
 * job list needs would be a request a second for no gain.
 */
export function workspaceExportsQuery(workspaceId: number) {
  return queryOptions({
    queryKey: qk.workspaceExports(workspaceId),
    queryFn: async (): Promise<WorkspaceExport[]> =>
      (
        await unwrap(
          getApi().GET('/api/workspaces/{workspace_id}/export/', {
            params: { path: { workspace_id: workspaceId } },
          }),
        )
      ).results,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (job) => job.state !== 'finished' && job.state !== 'failed',
      )
        ? 3_000
        : false,
  })
}

export function useWorkspaceExports(workspaceId: number) {
  return useQuery(workspaceExportsQuery(workspaceId))
}

export function exportWorkspaceMutation(queryClient: QueryClient, workspaceId: number) {
  return mutationOptions({
    mutationFn: (body: WorkspaceExportRequest): Promise<Job> =>
      unwrap(
        getApi().POST('/api/workspaces/{workspace_id}/export/async/', {
          params: { path: { workspace_id: workspaceId } },
          // Cast because the generated body carries two read-only fields the
          // job type's response mixin leaks into it -- see
          // `WorkspaceExportRequest`.
          body: body as never,
        }),
      ) as Promise<Job>,
    onSuccess: async (job) => {
      pushJob(queryClient, job)
      // And into the list beside it, which is where the archive will appear:
      // the two are the same job seen twice, and only this list carries the url.
      await queryClient.invalidateQueries({ queryKey: qk.workspaceExports(workspaceId) })
    },
  })
}

export function useExportWorkspace(workspaceId: number) {
  return useMutation(exportWorkspaceMutation(useQueryClient(), workspaceId))
}

/**
 * Uploading an archive, which is `multipart/form-data`.
 *
 * The same arrangement the file-field upload uses, and the client needs telling
 * none of it: it passes a `FormData` through its serialiser untouched and
 * leaves the content type unset, because the boundary in it is the browser's to
 * write and cannot be written without the body.
 */
export function uploadImportMutation(workspaceId: number) {
  return mutationOptions({
    mutationFn: (file: File): Promise<ImportResource> => {
      const body = new FormData()
      body.append('file', file)
      return unwrap(
        getApi().POST('/api/workspaces/{workspace_id}/import/upload-file/', {
          params: { path: { workspace_id: workspaceId } },
          // The contract describes the multipart body as one `file` part, which
          // is what it is -- but a `FormData` is not that object, it is the
          // encoding of it, and only the serialiser below sees the difference.
          body: body as unknown as { file: string },
          // Passed straight through: the boundary in a `FormData` is the
          // browser's to write, and it cannot be written without the body.
          bodySerializer: (form: unknown) => form as BodyInit,
        }),
      )
    },
  })
}

export function useUploadImport(workspaceId: number) {
  return useMutation(uploadImportMutation(workspaceId))
}

export function importWorkspaceMutation(queryClient: QueryClient, workspaceId: number) {
  return mutationOptions({
    mutationFn: (resourceId: number): Promise<Job> =>
      unwrap(
        getApi().POST('/api/workspaces/{workspace_id}/import/async/', {
          params: { path: { workspace_id: workspaceId } },
          body: { resource_id: resourceId },
        }),
      ) as Promise<Job>,
    onSuccess: (job) => {
      pushJob(queryClient, job)
      // Nothing else is invalidated here. The applications arrive as the job
      // finishes, over the socket, the way a duplicated one does -- and
      // invalidating now would refetch a workspace the import has not written
      // to yet.
    },
  })
}

export function useImportWorkspace(workspaceId: number) {
  return useMutation(importWorkspaceMutation(useQueryClient(), workspaceId))
}

/** Throws away an uploaded archive that was never imported. */
export function discardImportMutation(workspaceId: number) {
  return mutationOptions({
    mutationFn: (resourceId: number) =>
      unwrap(
        getApi().DELETE('/api/workspaces/{workspace_id}/import/{resource_id}/', {
          params: { path: { workspace_id: workspaceId, resource_id: resourceId } },
        }),
      ),
  })
}

export function useDiscardImport(workspaceId: number) {
  return useMutation(discardImportMutation(workspaceId))
}
